<!-- Zoom and crop a picture to the square the board draws.

     Shown after choosing an image on the web, and after the NATIVE picker when
     that picker had no crop step of its own. Drag to move, pinch or slide to
     zoom; the circle is exactly what ends up on the maze. -->
<template>
  <div class="sheet" role="dialog" aria-modal="true" aria-label="Position the picture">
    <div class="panel">
      <p class="title">Position {{ label }}</p>

      <div
        ref="stage"
        class="stage"
        :style="{ width: `${SIZE}px`, height: `${SIZE}px` }"
        @pointerdown="grab"
        @pointermove="drag"
        @pointerup="drop"
        @pointercancel="drop"
        @wheel.prevent="wheel"
      >
        <img v-if="src" :src="src" :style="imgStyle" alt="" draggable="false">
        <div class="mask" aria-hidden="true" />
      </div>

      <div class="zoom">
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <circle cx="11" cy="11" r="6" fill="none" stroke="currentColor" stroke-width="2" />
          <path d="M15.5 15.5 L20 20" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
        <input
          type="range" :min="ZOOM_MIN" :max="ZOOM_MAX" step="0.01" :value="crop.zoom"
          aria-label="Zoom" @input="setZoom($event.target.value)"
        >
      </div>

      <div class="actions">
        <button type="button" class="ghost" @click="$emit('cancel')">Cancel</button>
        <button type="button" class="use" @click="use">Use this</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import {
  DEFAULT_CROP, ZOOM_MIN, ZOOM_MAX, coverScale, clampOffset, cropToSquare, loadImage,
} from '../game/faces.js';

const props = defineProps({
  file: { type: Object, required: true },
  label: { type: String, default: 'the picture' },
});
const emit = defineEmits(['cancel', 'done', 'error']);

/** The preview is square and fixed, so the crop maths has one unit to work in. */
const SIZE = 240;

const src = ref('');
const stage = ref(null);
const crop = reactive({ ...DEFAULT_CROP });
let image = null;
let revoke = null;
let pointer = null;

const imgStyle = computed(() => {
  if (!image) return {};
  const scale = coverScale(image.naturalWidth, image.naturalHeight, SIZE) * crop.zoom;
  const w = image.naturalWidth * scale;
  const h = image.naturalHeight * scale;
  return {
    width: `${w}px`,
    height: `${h}px`,
    left: `${(SIZE - w) / 2 + crop.x}px`,
    top: `${(SIZE - h) / 2 + crop.y}px`,
  };
});

function settle() {
  if (!image) return;
  Object.assign(crop, clampOffset(crop, image.naturalWidth, image.naturalHeight, SIZE));
}

const setZoom = (v) => { crop.zoom = Number(v); settle(); };
const wheel = (e) => { crop.zoom -= e.deltaY * 0.002; settle(); };

function grab(e) {
  pointer = { id: e.pointerId, x: e.clientX, y: e.clientY };
  stage.value?.setPointerCapture?.(e.pointerId);
}
function drag(e) {
  if (!pointer || e.pointerId !== pointer.id) return;
  crop.x += e.clientX - pointer.x;
  crop.y += e.clientY - pointer.y;
  pointer = { id: e.pointerId, x: e.clientX, y: e.clientY };
  settle();
}
const drop = () => { pointer = null; };

function use() {
  if (!image) return;
  try { emit('done', cropToSquare(image, crop, SIZE)); }
  catch { emit('error', 'That image could not be prepared.'); }
}

onMounted(async () => {
  try {
    const loaded = await loadImage(props.file);
    image = loaded.img;
    revoke = loaded.revoke;
    src.value = image.src;
    settle();
  } catch (e) {
    emit('error', e.message);
  }
});
onBeforeUnmount(() => revoke?.());
</script>

<style scoped>
.sheet {
  position: fixed; inset: 0; z-index: 40; display: grid; place-items: center;
  padding: 20px; background: rgb(4 6 12 / 88%);
}
.panel {
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  padding: 20px; max-width: 320px; width: 100%;
  background: var(--rm-panel); border: 1px solid var(--rm-edge); border-radius: 16px;
}
.title { margin: 0; font-size: 14px; font-weight: 700; color: var(--rm-ink); }

.stage {
  position: relative; overflow: hidden; touch-action: none; cursor: grab;
  background: #05070f; border-radius: 12px;
}
.stage:active { cursor: grabbing; }
.stage img { position: absolute; user-select: none; -webkit-user-drag: none; }
/* Everything outside the circle dimmed, so what you see is what lands. */
.mask {
  position: absolute; inset: 0; pointer-events: none;
  background: rgb(5 7 15 / 72%);
  -webkit-mask-image: radial-gradient(circle at 50% 50%, transparent 49.5%, #000 50%);
          mask-image: radial-gradient(circle at 50% 50%, transparent 49.5%, #000 50%);
}
.mask::after {
  content: ''; position: absolute; inset: 0; margin: auto;
  width: 100%; height: 100%; border-radius: 50%;
  box-shadow: inset 0 0 0 1.5px var(--rm-cyan);
}

.zoom { display: flex; align-items: center; gap: 10px; width: 100%; color: var(--rm-dim); }
.zoom input {
  flex: 1; appearance: none; height: 3px; border-radius: 2px;
  background: var(--rm-edge); cursor: pointer;
}
.zoom input::-webkit-slider-thumb {
  appearance: none; width: 17px; height: 17px; border-radius: 50%;
  background: var(--rm-cyan); border: 0; cursor: pointer;
}

.actions { display: flex; gap: 10px; width: 100%; }
.actions button {
  flex: 1; font: inherit; font-size: 14px; font-weight: 700; padding: 12px 0;
  cursor: pointer; border-radius: 10px;
}
.ghost { color: var(--rm-dim); background: none; border: 1px solid var(--rm-edge); }
.use { color: var(--rm-bg); background: var(--rm-cyan); border: 0; }
</style>
